/**
 * Composer.js — message input bar with rich features.
 *
 * Features:
 * - Text input with auto-grow
 * - Enter to send, Shift+Enter for newline
 * - Reply-to context bar
 * - Attach menu (image/video/audio/document/location/contact)
 * - Emoji shelf
 * - Optimistic + status updates via messages-service
 */

import { html } from 'htm/preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { useStore } from '@nanostores/preact';

import { $sheets, openSheet, showToast, $openChatPhone, $openChatKey, consumePendingShare } from '../stores/ui.js';
import { sendMedia, sendLocation, sendContact } from '../services/messages-service.js';
import { matchQuickReplyCommand } from '../services/quick-replies.js';
import { LocationFormSheet } from './LocationFormSheet.js';
import { ContactFormSheet }  from './ContactFormSheet.js';
import { MediaFormSheet }    from './MediaFormSheet.js';
import { SchedulePopover }   from './SchedulePopover.js';

// Per-row draft persistence — each chat row gets its own unsent-text buffer
// keyed by composite chat_key (contactId:channelId) in localStorage.
// #153: keying by phone alone would leak drafts between the two per-channel
// rows for the same contact (e.g. a Prinstaindia half-draft would appear in
// the ODIV composer). Keyed by chat_key instead so each row has its own.
const DRAFTS_KEY = 'inbox_v2_drafts';
function _loadDrafts() {
  try { return JSON.parse(localStorage.getItem(DRAFTS_KEY) || '{}'); } catch { return {}; }
}
function _getDraft(key) {
  if (!key) return '';
  return _loadDrafts()[key] || '';
}
function _saveDraft(key, text) {
  if (!key) return;
  try {
    const all = _loadDrafts();
    if (text) all[key] = text;
    else      delete all[key];
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(all));
  } catch { /* quota */ }
}

const QUICK_EMOJIS = ['😀','😂','😍','🤔','😢','😡','👍','👎','❤️','🔥','✅','❌','🎉','🙏','💪','👋','🤝','💯','⭐','🚀','💬','📱','📧','💡','🔔','⚠️','✨','🎯'];

export function Composer({ onSend, onSchedule, replyTo, onCancelReply, disabled = false, disabledReason = null, channelPhoneId = null, lastInboundTs = null, prefill = null }) {
  const sheets  = useStore($sheets);
  const phone   = useStore($openChatPhone); // for payload recipient
  const chatKey = useStore($openChatKey);   // #153: per-row draft key
  // Initialize from the stored draft for this row so opening a chat you
  // half-typed earlier restores your text.
  const [text, setText] = useState(() => _getDraft($openChatKey.get()));
  const [sending, setSending] = useState(false);
  // Media upload progress state — for the "uploading 47%" indicator that
  // appears in the composer while a file is being read + sent. Closes the
  // "looks frozen for big videos" UX gap from the code review.
  const [uploadingFile, setUploadingFile] = useState(null);  // { name, type } or null
  // Location + Contact + Media caption inline forms — null when closed,
  // object when open. These live in Composer state (not $sheets) because
  // they're transient data entry bound to the current send, not a global
  // UI toggle.
  const [locationForm, setLocationForm] = useState(null);
  // shape: { latitude, longitude, name, address, pending, error }
  const [contactForm, setContactForm] = useState(null);
  // shape: { firstName, lastName, phoneNumber, email, company, title, pending }
  // #151: After picking a file, we no longer send immediately — instead
  // we open a preview sheet where the agent can add a caption (or skip
  // it) and then tap Send. This matches the WhatsApp / native client UX
  // and uses the `image.caption` / `video.caption` / `document.caption`
  // fields already documented in the CRM Postman collection.
  const [mediaForm, setMediaForm] = useState(null);
  // shape: { file, previewUrl, type, caption, pending }
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const longPressTimer = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // When the user switches rows, load THAT row's draft. The effect fires
  // whenever $openChatKey changes. #153: key by chatKey so a draft typed
  // into Sambit's Prinstaindia row doesn't bleed into the ODIV row.
  //
  // #193 — Web Share Target. If $pendingShare is set when this Composer
  // mounts (or the agent navigates to a chat after picking from the
  // share banner), drain the share into the input. Append to any
  // existing draft so we don't clobber an in-progress message.
  useEffect(() => {
    let next = _getDraft(chatKey);
    const share = consumePendingShare();
    if (share?.text) {
      next = next ? `${next}\n${share.text}` : share.text;
      showToast('Shared content added to composer', 1800);
    }
    setText(next);
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
      }
    });
  }, [chatKey]);

  // Persist draft on every text change. Cheap — localStorage writes take
  // under 1ms and drafts are tiny strings. No debounce needed at this scale.
  useEffect(() => {
    if (chatKey !== null) _saveDraft(chatKey, text);
  }, [text, chatKey]);

  // T8 — Send-now prefill from a scheduled ghost bubble. Thread passes a
  // `{ text, nonce }` signal whenever the agent taps the Send-now icon;
  // the nonce changes per-invocation so this effect fires even if the
  // same text is sent-now twice in a row. We overwrite the textarea (no
  // append / concat) because the whole point of Send-now is "this is the
  // message I want to fire right now" — anything the user had half-typed
  // would be ambiguous. Auto-focus + grow so one more tap sends.
  useEffect(() => {
    if (!prefill || typeof prefill.text !== 'string') return;
    setText(prefill.text);
    if (chatKey !== null) _saveDraft(chatKey, prefill.text);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
      try { el.setSelectionRange(prefill.text.length, prefill.text.length); } catch {}
    });
  }, [prefill?.nonce]);

  // ── Quick-reply (/slash) expansion ────────────────────────────────────
  // Tracks the current caret position so matchQuickReplyCommand can scan
  // backward from it to find the slash-token under the cursor. Updated on
  // every keyup / selectionchange style event via onSelect on the textarea.
  const [caret, setCaret] = useState(0);
  // activeIdx = which suggestion is currently highlighted (0-indexed).
  // Reset to 0 whenever the match list changes. Keyboard nav (arrow / Tab /
  // Enter) drives this; mouse click bypasses it.
  const [activeIdx, setActiveIdx] = useState(0);

  const matchResult = matchQuickReplyCommand(text, caret);
  const suggestions = matchResult.matches || [];
  const showSuggestions = suggestions.length > 0 && !disabled;

  // Expand a chosen quick reply into the textarea, replacing the
  // slash-token that's under the caret with the reply's body + a trailing
  // space so the agent can keep typing. Focus + caret land at the end of
  // the expanded text.
  const expandQuickReply = (reply) => {
    if (!reply || matchResult.token == null) return;
    const { wordStart, wordEnd } = matchResult;
    const before = text.slice(0, wordStart);
    const after  = text.slice(wordEnd);
    const expanded = reply.text + (after.startsWith(' ') ? '' : ' ');
    const next = before + expanded + after;
    setText(next);
    setActiveIdx(0);
    // Put caret at the end of the inserted text so continued typing picks
    // up right after the canned response.
    const nextCaret = before.length + expanded.length;
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(nextCaret, nextCaret); } catch {}
      // Also grow the textarea to fit the new content
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
      setCaret(nextCaret);
    });
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending || disabled) return;
    // Stash the text BEFORE clearing the input, so we can restore on failure.
    // Without this, a network error eats the user's typing and they have to
    // re-type from memory.
    const stashedText = trimmed;
    const stashedReply = replyTo;
    setSending(true);
    setText('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    try {
      await onSend(stashedText, stashedReply);
      // Only clear the reply context if the send succeeded.
      if (onCancelReply) onCancelReply();
    } catch (err) {
      // Restore the text the user typed so they don't lose it. The reply
      // context stays intact too — they can retry by tapping Send again.
      setText(stashedText);
      // Reset textarea height after the next render
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
          inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
          inputRef.current.focus();
        }
      });
      showToast(`Send failed: ${err.message || 'network error'}. Tap Send to retry.`, 4500);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    // Quick-reply suggestions take keyboard priority when visible:
    //   ArrowDown / ArrowUp → move highlight
    //   Tab / Enter → expand the highlighted suggestion
    //   Escape → dismiss (by pretending the token doesn't match — simplest
    //            is to just drop the first char of the slash token so the
    //            match list goes empty; users can also just keep typing)
    if (showSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        expandQuickReply(suggestions[activeIdx] || suggestions[0]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        // Force the match list empty by rewriting the current slash token
        // so it starts with a non-slash character. Caret stays put.
        const { wordStart, wordEnd } = matchResult;
        const next = text.slice(0, wordStart) + text.slice(wordStart + 1, wordEnd) + text.slice(wordEnd);
        setText(next);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e) => {
    setText(e.target.value);
    setCaret(e.target.selectionStart ?? e.target.value.length);
    setActiveIdx(0);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const handleSelect = (e) => {
    // Keep caret state fresh on cursor moves + clicks so the slash-scan
    // uses the right position even when the user is editing mid-text.
    setCaret(e.target.selectionStart ?? 0);
  };

  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    if (!el) { setText(text + emoji); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = text.substring(0, start) + emoji + text.substring(end);
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + emoji.length;
    });
  };

  const openAttach   = () => openSheet('attach');
  const closeAttach  = () => $sheets.setKey('attach', false);
  const toggleEmoji  = () => $sheets.setKey('emoji', !sheets.emoji);

  // #151: file pick no longer sends — it opens a preview sheet where the
  // agent can add an optional caption. The actual upload happens in
  // submitMediaForm below.
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (disabled) {
      showToast(disabledReason || 'Cannot send right now', 3000);
      return;
    }
    closeAttach();
    const type = file.type.startsWith('image/') ? 'image'
               : file.type.startsWith('video/') ? 'video'
               : file.type.startsWith('audio/') ? 'audio'
               : 'document';
    // Image/video get an inline preview via blob URL. Audio/document
    // show filename + icon only. The blob URL is revoked in closeMediaForm.
    const previewUrl = (type === 'image' || type === 'video')
      ? URL.createObjectURL(file)
      : null;
    setMediaForm({ file, previewUrl, type, caption: '', pending: false });
  };

  const closeMediaForm = () => {
    if (mediaForm?.previewUrl) {
      try { URL.revokeObjectURL(mediaForm.previewUrl); } catch { /* noop */ }
    }
    setMediaForm(null);
  };

  const submitMediaForm = async () => {
    const f = mediaForm;
    if (!f || f.pending) return;
    const phone = $openChatPhone.get();
    if (!phone) return;
    // Audio never carries a caption per Meta spec — ignore whatever the
    // UI collected (we also hide the input for audio below).
    const caption = f.type === 'audio' ? '' : f.caption;
    // R2a — carry the reply-target wamid through so sendMedia sets
    // context.message_id on the Meta send. Stash the wamid BEFORE the
    // await so a mid-flight replyTo cancel (e.g. user tapping the ×)
    // doesn't drop the context we meant to honor.
    const stashedReplyWamid = replyTo?.wamid || null;
    setMediaForm({ ...f, pending: true });
    setUploadingFile({ name: f.file.name, type: f.file.type, size: f.file.size });
    try {
      await sendMedia(phone, f.file, caption, stashedReplyWamid);
      // Success — optimistic bubble already reflects the caption, the
      // preview, and the reply context; drop the form, the upload
      // badge, and clear the reply pill just like sendText does.
      closeMediaForm();
      if (stashedReplyWamid && onCancelReply) onCancelReply();
    } catch (err) {
      console.error('[v2 composer] sendMedia failed:', err.message);
      showToast(`Upload failed: ${err.message || 'network error'}`, 4500);
      setMediaForm((cur) => cur ? { ...cur, pending: false } : cur);
    } finally {
      setUploadingFile(null);
    }
  };

  // ── Location form helpers ───────────────────────────────────────────
  const openLocationForm = () => {
    closeAttach();
    setLocationForm({ latitude: '', longitude: '', name: '', address: '', pending: false });
  };
  const closeLocationForm = () => setLocationForm(null);

  /**
   * Try to fill lat/lng from the browser's geolocation API. Fails silently
   * with a toast if permission is denied or the service is unavailable —
   * the user can still fill lat/lng manually.
   */
  const fillCurrentLocation = () => {
    if (!('geolocation' in navigator)) {
      showToast('Geolocation not supported on this device', 2500);
      return;
    }
    setLocationForm((f) => f ? { ...f, pending: true } : f);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationForm((f) => f ? {
          ...f,
          latitude:  pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
          pending:   false,
        } : f);
      },
      (err) => {
        setLocationForm((f) => f ? { ...f, pending: false } : f);
        showToast(`Location unavailable: ${err.message || 'denied'}`, 3000);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  const submitLocation = async () => {
    const f = locationForm;
    if (!f || f.pending) return;
    const latitude  = parseFloat(f.latitude);
    const longitude = parseFloat(f.longitude);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      showToast('Latitude and longitude must be numbers', 3000);
      return;
    }
    const phone = $openChatPhone.get();
    if (!phone) return;
    setLocationForm({ ...f, pending: true });
    try {
      await sendLocation(phone, {
        latitude,
        longitude,
        name:    f.name.trim(),
        address: f.address.trim(),
      });
      setLocationForm(null);
    } catch (e) {
      showToast(`Send failed: ${e.message}`, 3500);
      setLocationForm({ ...f, pending: false });
    }
  };

  // ── Contact form helpers ────────────────────────────────────────────
  const openContactForm = () => {
    closeAttach();
    setContactForm({
      firstName: '', lastName: '', phoneNumber: '',
      email: '', company: '', title: '', pending: false,
    });
  };
  const closeContactForm = () => setContactForm(null);

  const submitContact = async () => {
    const f = contactForm;
    if (!f || f.pending) return;
    if (!f.firstName.trim() || !f.phoneNumber.trim()) {
      showToast('Name and phone are required', 2500);
      return;
    }
    const phone = $openChatPhone.get();
    if (!phone) return;
    setContactForm({ ...f, pending: true });
    try {
      await sendContact(phone, {
        firstName:   f.firstName.trim(),
        lastName:    f.lastName.trim(),
        phoneNumber: f.phoneNumber.trim(),
        email:       f.email.trim(),
        company:     f.company.trim(),
        title:       f.title.trim(),
      });
      setContactForm(null);
    } catch (e) {
      showToast(`Send failed: ${e.message}`, 3500);
      setContactForm({ ...f, pending: false });
    }
  };

  // Pretty file size string for the upload indicator
  const _fmtBytes = (n) => {
    if (!n) return '';
    if (n < 1024) return n + ' B';
    if (n < 1024*1024) return Math.round(n/1024) + ' KB';
    return (n/(1024*1024)).toFixed(1) + ' MB';
  };

  const triggerFilePick = (accept) => {
    if (!fileInputRef.current) return;
    fileInputRef.current.accept = accept;
    fileInputRef.current.click();
  };

  // The position:static override is load-bearing. inbox/css/inbox.css
  // (the V1 stylesheet, still loaded via the SW shell) sets
  //   .inbox-composer-bar { position: absolute; bottom: 0; left: 0; right: 0 }
  // which pulls the bar out of flow. In V2 that makes every flow sibling
  // above it — the reply bar, upload progress indicator, /slash quick-reply
  // dropdown, and emoji shelf — collapse into zero layout height and get
  // painted over by the absolute bar. The dropdown appears in the DOM but
  // is invisible. We don't want to edit inbox.css since V1 still relies on
  // the absolute positioning, so we override position inline on the V2
  // element only. This is what makes the /slash dropdown + reply bar +
  // upload progress + emoji shelf all visually render above the composer
  // input. See task #106-followup / the "quick reply /slash not popping
  // up" bug report.
  const composerBarStyle = disabled
    ? 'position:static;opacity:0.6;pointer-events:auto'
    : 'position:static';

  return html`
    <div>
      <!-- Reply context bar — visually mirrors the bubble quote style -->
      ${replyTo && html`
        <div class="inbox-reply-bar" style="display:flex;align-items:flex-start;gap:8px;padding:8px 12px;background:rgba(255,255,255,0.05);border-top:1px solid rgba(255,255,255,0.08);border-left:3px solid #25D366">
          <div style="flex:1;display:flex;flex-direction:column;gap:2px;min-width:0">
            <span style="font-size:0.7rem;font-weight:600;color:#25D366;letter-spacing:0.02em">
              Replying to ${replyTo.fromMe ? 'yourself' : 'message'}
            </span>
            <span style="font-size:0.78rem;opacity:0.9;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
              ${replyTo.preview || '[Message]'}
            </span>
          </div>
          <button
            onClick=${onCancelReply}
            class="inbox-reply-cancel"
            style="background:none;border:none;color:#7d8799;font-size:1.4rem;cursor:pointer;padding:0 4px;line-height:1"
            title="Cancel reply"
          >×</button>
        </div>
      `}

      <!-- Media upload-in-progress badge -->
      ${uploadingFile && html`
        <div
          class="inbox-upload-progress"
          style="display:flex;align-items:center;gap:10px;padding:10px 14px;background:rgba(37,211,102,0.08);border-top:1px solid rgba(37,211,102,0.25);border-bottom:1px solid rgba(37,211,102,0.25);font-size:0.8rem;color:#fff"
        >
          <div class="inbox-spinner" style="width:14px;height:14px;border:2px solid rgba(37,211,102,0.3);border-top-color:#25D366;border-radius:50%;animation:v2-spin 0.8s linear infinite"></div>
          <div style="flex:1;display:flex;flex-direction:column;gap:2px;min-width:0">
            <span style="font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
              Uploading ${uploadingFile.name}
            </span>
            <span style="font-size:0.7rem;opacity:0.7">
              ${_fmtBytes(uploadingFile.size)}${uploadingFile.size > 4_000_000 ? ' — large file, this may take a moment' : ''}
            </span>
          </div>
        </div>
      `}

      <!-- Quick-reply suggestion dropdown — appears above the composer
           whenever the slash-token under the caret matches at least one
           saved reply. Keyboard nav (↑↓ / Tab / Enter) drives highlight,
           tap picks directly. Escape or a non-slash keystroke dismisses.
           -->
      ${showSuggestions && html`
        <div
          class="inbox-quickreply-menu"
          role="listbox"
          aria-label="Quick replies"
          style="background:rgba(10,12,18,0.98);border-top:1px solid rgba(255,255,255,0.12);border-bottom:1px solid rgba(255,255,255,0.08);max-height:220px;overflow-y:auto;backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px)"
        >
          ${suggestions.map((r, i) => {
            const active = i === activeIdx;
            // #170 — visual differentiation between shared (admin) + personal.
            // Shared = blue tint + lock icon next to shortcut so the agent
            // knows it came from the boss. Personal = green tint (brand
            // accent) so it reads as "your own".
            const isShared = (r.scope || 'account') === 'account';
            const pillBg     = isShared ? 'rgba(96,165,250,0.15)'  : 'rgba(37,211,102,0.15)';
            const pillBorder = isShared ? 'rgba(96,165,250,0.45)'  : 'rgba(37,211,102,0.35)';
            const pillColor  = isShared ? '#93c5fd'                : '#25D366';
            return html`
              <button
                key=${r.id}
                role="option"
                aria-selected=${active}
                onMouseDown=${(e) => { e.preventDefault(); expandQuickReply(r); }}
                onMouseEnter=${() => setActiveIdx(i)}
                class="inbox-quickreply-row"
                style="display:flex;align-items:flex-start;gap:10px;width:100%;padding:10px 14px;background:${active ? 'rgba(37,211,102,0.12)' : 'transparent'};border:none;border-left:3px solid ${active ? '#25D366' : 'transparent'};color:inherit;cursor:pointer;text-align:left"
                title=${isShared ? 'Shared reply (set by admin)' : 'Your personal reply'}
              >
                <span style=${`flex-shrink:0;display:inline-flex;align-items:center;gap:4px;background:${pillBg};border:1px solid ${pillBorder};color:${pillColor};border-radius:6px;padding:2px 7px;font-size:0.66rem;font-weight:700;font-family:ui-monospace,Menlo,monospace;margin-top:1px`}>
                  ${isShared && html`
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  `}
                  /${r.shortcut}
                </span>
                <span style="flex:1;min-width:0;font-size:0.8rem;line-height:1.4;color:var(--v2-text, #f1f4f9);overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;white-space:pre-wrap">
                  ${r.text}
                </span>
              </button>
            `;
          })}
          <div style="padding:6px 14px;font-size:0.62rem;color:var(--text-muted, #7d8799);border-top:1px solid rgba(255,255,255,0.06);background:rgba(0,0,0,0.2)">
            ↑↓ navigate · Tab / Enter to insert · Esc to dismiss
          </div>
        </div>
      `}

      <!-- Emoji shelf -->
      ${sheets.emoji && html`
        <div class="inbox-emoji-shelf" style="display:flex">
          ${QUICK_EMOJIS.map(e => html`
            <button key=${e} class="inbox-emoji-btn" onClick=${() => insertEmoji(e)}>${e}</button>
          `)}
        </div>
      `}

      <!-- Composer bar. The position:static override is load-bearing —
           see the long JS comment on composerBarStyle above the return. -->
      <div
        class="inbox-composer-bar"
        style=${composerBarStyle}
      >
        <button
          class="inbox-composer-icon-btn"
          onClick=${openAttach}
          title=${disabled ? (disabledReason || 'Cannot attach right now') : 'Attach'}
          disabled=${disabled}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.2-9.19a4 4 0 0 1 5.65 5.65l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
          </svg>
        </button>
        <textarea
          ref=${inputRef}
          class="inbox-composer-input"
          placeholder=${disabled ? (disabledReason || 'Sending is disabled') : 'Type a message…'}
          rows="1"
          value=${text}
          onInput=${handleInput}
          onKeyDown=${handleKeyDown}
          onSelect=${handleSelect}
          onClick=${handleSelect}
          disabled=${sending || disabled}
        ></textarea>
        <button
          class="inbox-composer-icon-btn"
          onClick=${toggleEmoji}
          title="Emoji"
          disabled=${disabled}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M8 13s1.5 2 4 2 4-2 4-2"/>
            <line x1="9" y1="9" x2="9.01" y2="9"/>
            <line x1="15" y1="9" x2="15.01" y2="9"/>
          </svg>
        </button>
        <div style="position:relative">
          <button
            class="inbox-composer-send-btn"
            onClick=${() => { if (!scheduleOpen) handleSend(); }}
            onTouchStart=${() => {
              if (disabled || !text.trim()) return;
              longPressTimer.current = setTimeout(() => { longPressTimer.current = null; setScheduleOpen(true); }, 500);
            }}
            onTouchEnd=${() => { if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; } }}
            onTouchCancel=${() => { if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; } }}
            onMouseDown=${() => {
              if (disabled || !text.trim()) return;
              longPressTimer.current = setTimeout(() => { longPressTimer.current = null; setScheduleOpen(true); }, 500);
            }}
            onMouseUp=${() => { if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; } }}
            onMouseLeave=${() => { if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; } }}
            disabled=${!text.trim() || sending || disabled}
            title=${disabled ? (disabledReason || 'Sending disabled') : 'Tap to send · Hold for schedule'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
          ${scheduleOpen && text.trim() && html`
            <${SchedulePopover}
              phone=${phone}
              channelPhoneId=${channelPhoneId}
              lastInboundTs=${lastInboundTs}
              onClose=${() => setScheduleOpen(false)}
              onScheduled=${(schedType, delayMs, triggerMs, basePayload) => {
                if (onSchedule) {
                  basePayload.text = { body: text.trim() };
                  onSchedule(schedType, delayMs, triggerMs, basePayload);
                  setText('');
                  _saveDraft(chatKey, '');
                  if (inputRef.current) inputRef.current.style.height = 'auto';
                }
              }}
            />
          `}
        </div>
      </div>

      <!-- Attach sheet -->
      ${sheets.attach && html`
        <div class="inbox-sheet-overlay" style="display:block" onClick=${closeAttach}></div>
        <div class="inbox-bottom-sheet inbox-attach-sheet" style="display:block" role="dialog" aria-modal="true" aria-label="Attach file">
          <div class="inbox-sheet-handle"></div>
          <div class="inbox-sheet-header">
            <h3 class="inbox-sheet-title">Attach</h3>
            <button class="inbox-sheet-close" onClick=${closeAttach}>×</button>
          </div>
          <div class="inbox-attach-options">
            <button class="inbox-attach-opt" onClick=${() => triggerFilePick('image/*,video/*')}>
              <span class="inbox-attach-icon">📷</span>
              <span>Photo / Video</span>
            </button>
            <button class="inbox-attach-opt" onClick=${() => triggerFilePick('.pdf,.doc,.docx,.txt,.xlsx,.csv')}>
              <span class="inbox-attach-icon">📄</span>
              <span>Document</span>
            </button>
            <button class="inbox-attach-opt" onClick=${() => triggerFilePick('audio/*')}>
              <span class="inbox-attach-icon">🎵</span>
              <span>Audio</span>
            </button>
            <button class="inbox-attach-opt" onClick=${openLocationForm}>
              <span class="inbox-attach-icon">📍</span>
              <span>Location</span>
            </button>
            <button class="inbox-attach-opt" onClick=${openContactForm}>
              <span class="inbox-attach-icon">👤</span>
              <span>Contact</span>
            </button>
            <button
              class="inbox-attach-opt"
              data-action="interactive"
              onClick=${() => { closeAttach(); openSheet('interactive'); }}
            >
              <span class="inbox-attach-icon">⚡</span>
              <span>Interactive</span>
            </button>
          </div>
        </div>
      `}

      <!-- Hidden file input -->
      <input
        type="file"
        ref=${fileInputRef}
        style="display:none"
        onChange=${handleFileChange}
      />

      <${LocationFormSheet}
        form=${locationForm}
        setForm=${setLocationForm}
        onClose=${closeLocationForm}
        onSubmit=${submitLocation}
        onFillCurrent=${fillCurrentLocation}
      />

      <${ContactFormSheet}
        form=${contactForm}
        setForm=${setContactForm}
        onClose=${closeContactForm}
        onSubmit=${submitContact}
      />

      <${MediaFormSheet}
        form=${mediaForm}
        setForm=${setMediaForm}
        onClose=${closeMediaForm}
        onSubmit=${submitMediaForm}
        fmtBytes=${_fmtBytes}
      />
    </div>
  `;
}
